import { TitleCasePipe } from '@angular/common';
import {
  Component,
  OnInit,
  ViewContainerRef,
  computed,
  effect,
  inject,
  viewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { Router } from '@angular/router';
import { GlobalStore } from '@maximizer/core/shared/data-access';
import { UserRights } from '@maximizer/core/shared/domain';
import { UI_KENDO } from '@maximizer/core/shared/ui/kendo';
import {
  ComposeStore,
  OutlookStore,
} from '@maximizer/outlook/shared/data-access';
import { EntryType } from '@maximizer/outlook/shared/domain';
import { MenuItem, TranslateVerticalPipe } from '@maximizer/outlook/shared/ui';
import { TranslatePipe } from '@ngx-translate/core';
import { KENDO_DROPDOWNBUTTON } from '@progress/kendo-angular-buttons';
import {
  KENDO_LISTVIEW,
  ListViewComponent,
  PagerSettings,
} from '@progress/kendo-angular-listview';
import { KENDO_TOOLTIP } from '@progress/kendo-angular-tooltip';

@Component({
  selector: 'maximizer-outlook-compose-add-entry',
  templateUrl: './add-entry.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    KENDO_DROPDOWNBUTTON,
    KENDO_LISTVIEW,
    KENDO_TOOLTIP,
    TitleCasePipe,
    TranslatePipe,
    TranslateVerticalPipe,
    UI_KENDO,
  ],
})
export class OutlookComposeAddEntryComponent implements OnInit {
  composeStore = inject(ComposeStore);
  outlookStore = inject(OutlookStore);
  globalStore = inject(GlobalStore);
  router = inject(Router);
  viewContainerRef = inject(ViewContainerRef);

  listView = viewChild<ListViewComponent>('listView');

  EntryType = EntryType;
  userRights?: UserRights;
  pageSize = 4;

  defaultPagerSettings: PagerSettings = {
    position: 'bottom',
    pageSizeValues: false,
    info: false,
    previousNext: true,
    type: 'numeric',
    buttonCount: 4,
  };

  showPagerNavigation = computed(() => {
    return this.composeStore.notInMaximizer().length > this.pageSize
      ? this.defaultPagerSettings
      : false;
  });

  addEntryMenuItems: MenuItem[] = [
    {
      id: 'individual',
      text: 'outlook.main.actions.addIndividual',
      route: '/abentry/add/individual',
      icon: 'fa-icons icon-regular icon-18 icon-building-user',
      disabled: false,
      title: 'outlook.entries.individuals',
    },
    {
      id: 'contact',
      text: 'outlook.main.actions.addContact',
      route: '/abentry/add/contact',
      icon: 'fa-icons icon-solid icon-18 icon-user',
      disabled: false,
      title: 'outlook.entries.contacts',
    },
    {
      id: 'lead',
      text: 'outlook.main.actions.addLead',
      route: '/lead/add',
      icon: 'fa-icons icon-solid icon-18 icon-user-magnifying-glass',
      disabled: false,
      title: 'outlook.entries.leads',
    },
  ];

  constructor() {
    effect(() => {
      // reset to first page when pager is not needed
      const itemCount = this.composeStore.notInMaximizer().length;
      const listViewComponent = this.listView();

      if (
        itemCount <= this.pageSize &&
        listViewComponent &&
        listViewComponent.skip > 0
      ) {
        listViewComponent.skip = 0;
      }
    });
  }

  ngOnInit(): void {
    this.setUserRights();
  }

  setUserRights(): void {
    if (this.globalStore.session()?.user) {
      this.userRights = this.globalStore.session()?.user.rights;

      if (!this.userRights?.lead?.insert) {
        const leadItem = this.addEntryMenuItems.find(
          (item) => item.id === EntryType.Lead,
        );
        if (leadItem) {
          leadItem.disabled = true;
        }
      }

      if (!this.userRights?.addressBook?.insert) {
        this.addEntryMenuItems.forEach((item) => {
          if (
            item.id === EntryType.Individual ||
            item.id === EntryType.Contact
          ) {
            item.disabled = true;
          }
        });
      }
    }
  }

  navigateToAdd(event: { route: string }, email: string, name: string): void {
    this.router.navigate([event.route], { queryParams: { email, name } });
  }
}
